import { useState } from 'react';
import { preflight } from '../app/coach';
import { log } from '../app/logger';
import { preflightNoticeB } from './preflightB';
import type { PreflightRunB, PreflightSurfaceB } from './preflightB';

/**
 * B-19 — **the *Test connection* control** (design spec §2.6), shared by its two surfaces.
 *
 * §2.6 puts this chip on the More tab *"and surfaced on match setup when purpose = team match"* —
 * the two moments a coach can still do something about a broken configuration: at a desk, and at
 * the pitch before kickoff. One component, because the alternative is two implementations of a
 * six-state answer that must never disagree, and because the sentence a coach reads the night
 * before must be the sentence they read at the touchline.
 *
 * ── Why it holds its own state, and resets rather than persists ──────────────
 *
 * A result is a fact about **one press, at one moment**, so it lives for as long as the control
 * is mounted and no longer: nothing here is written down, and a remount (which is how the
 * credential card discards a stale verdict after the credential changes) starts at *Not tested*.
 * A chip that remembered "Connected" from yesterday would be the pre-mortem's failure with a
 * longer fuse.
 *
 * `preflight` maps every path to an outcome, so the `catch` below is a defect channel rather than
 * a network one — surfaced as itself instead of dressed up as an unreachable service.
 *
 * ── B-24: the chip is told which surface it is on ───────────────────────────
 *
 * Two of the six sentences point at the credential field, which exists on one of these two screens
 * and not the other. The component that renders the chip is the only thing that knows which screen
 * that is, so it says — `surface` is required, and TypeScript makes a new render site answer the
 * question rather than inherit the More tab's answer by omission.
 */

const MODULE = 'ui-b/preflight';

export interface PreflightChipBProps {
  /** Which screen is rendering it — decides where the copy says the credential field is (§2.6). */
  readonly surface: PreflightSurfaceB;
  /** Copy under the control saying why it is offered *here*. Each surface has its own reason. */
  readonly note: string;
}

export function PreflightChipB({ surface, note }: PreflightChipBProps) {
  const [run, setRun] = useState<PreflightRunB>({ kind: 'idle' });
  const [problem, setProblem] = useState<string | null>(null);

  const test = (): void => {
    setRun({ kind: 'testing' });
    setProblem(null);
    void preflight()
      .then((outcome) => {
        setRun({ kind: 'result', outcome });
      })
      .catch((cause: unknown) => {
        log.error(MODULE, 'B §2.6: the pre-flight threw instead of reporting', {
          message: cause instanceof Error ? cause.message : String(cause),
        });
        setRun({ kind: 'idle' });
        setProblem('The connection test failed unexpectedly. The log has the detail.');
      });
  };

  const notice = run.kind === 'result' ? preflightNoticeB(run.outcome, surface) : null;

  return (
    <div className="more-preflight-block" data-testid="preflight">
      <div className="more-preflight">
        <button
          type="button"
          className="more-btn"
          data-testid="preflight-test"
          disabled={run.kind === 'testing'}
          onClick={test}
        >
          Test connection
        </button>
        <span
          className="more-chip"
          data-testid="preflight-chip"
          data-state={run.kind === 'result' ? run.outcome.kind : run.kind}
          data-tone={notice === null ? run.kind : notice.tone}
          role="status"
        >
          {run.kind === 'idle' && 'Not tested'}
          {run.kind === 'testing' && 'Testing…'}
          {notice !== null && notice.label}
        </span>
      </div>
      {notice !== null && (
        <p className="more-preflight__detail" data-testid="preflight-detail">
          {notice.detail}
        </p>
      )}
      {problem !== null && (
        <p className="more-problem" data-testid="preflight-problem" role="status">
          {problem}
        </p>
      )}
      <p className="more-card__note">{note}</p>
    </div>
  );
}
